import { AdductionOrder } from '@features/siteStatusV2/types';
import {
  ChatFill,
  Clipboard2CheckFill,
  ClockFill,
  EnvelopeAt,
  GeoAltFill,
  PersonFill,
  RssFill,
  ShieldFillExclamation,
  TelephoneFill
} from 'react-bootstrap-icons';
import { getI18n } from 'react-i18next';

import { TranslationNamespaces } from '../../../../../locales/constants/translationNamespaces.enum';
import { ConfigOrderDetails } from '../../order-details';
import AdductionStep from '../AdductionStep';
import { convertDateFormat } from '@features/siteStatusV2/utils/helpers';

const createAdductionDisplayConfig = (order: AdductionOrder): ConfigOrderDetails => {
  const { t } = getI18n();

  const traduction = (s1: string, s2?: string) => {
    return s2
      ? t(s1, s2, { ns: TranslationNamespaces.SITE_STATUS })
      : t(s1, { ns: TranslationNamespaces.SITE_STATUS });
  };

  return {
    header: {
      name: order.name,
      headerBadges: [
        {
          color: 'grey',
          label: `${traduction('orderDetails.adduction.orderDate')} : ${convertDateFormat(
            order.createdDate
          )}`,
          icon: { iconName: 'solid-clock' }
        }
      ]
    },
    sections: [
      {
        custom: <AdductionStep order={order} />,
        isVisible: true,
        shouldExport: false
      },
      {
        isVisible: false,
        shouldExport: true,
        fields: [
          {
            title: traduction('orderDetails.generalInformations.referenceServiceTaken'),
            icon: 'rss',
            value: { label: order.name ?? '' }
          },
          {
            title: traduction('OrderList.orderDate'),
            icon: 'light-bulb',
            value: { label: convertDateFormat(order.createdDate) }
          }
        ]
      },
      {
        title: traduction('orderDetails.adduction.title'),
        isVisible: true,
        shouldExport: true,
        fields: [
          {
            title: traduction('orderDetails.adduction.requester'),
            icon: <PersonFill />,
            value: {
              label: order.administrativeData.name ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.phone'),
            icon: <TelephoneFill />,
            value: {
              label: order.administrativeData.accountPhone ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.email'),
            icon: <EnvelopeAt />,
            value: {
              label: order.administrativeData.accountEmailAddress ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.address'),
            icon: <GeoAltFill />,
            value: {
              label: order.administrativeData.accountAddress ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.postalCode'),
            icon: <GeoAltFill />,
            value: {
              label: order.administrativeData.postalCode ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.city'),
            icon: <GeoAltFill />,
            value: {
              label: order.administrativeData.city ?? ''
            }
          }
        ]
      },
      {
        isVisible: false,
        shouldExport: true,
        fields: [
          {
            title: traduction('orderDetails.adduction.studyPaymentDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.fieldVisit.studyPaymentDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.siteVisitAppointmentDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.fieldVisit.siteVisitAppointmentDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.vtDelayCause'),
            icon: <ShieldFillExclamation />,
            value: {
              label: order?.fieldVisit.vtDelayCause ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.vtDelayResponsible'),
            icon: <PersonFill />,
            value: {
              label: order?.fieldVisit.vtDelayResponsible ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.estimateSendDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.quote.quoteSendDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.estimateStatus'),
            icon: <RssFill />,
            value: {
              label: order?.quote.quoteStatus ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.estimatePaymentDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.quote.quotePaymentDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.clientRejection'),
            icon: <ChatFill />,
            value: {
              label: order?.quote.clientRejection ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.estimatedWorkDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.quote.quoteWorkDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.depositReceiptDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.works.depositReceiptDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.workStatus'),
            icon: <RssFill />,
            value: {
              label: order?.works.workStatus ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.workStartDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.works.workStartDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.workEndDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.works.workEndDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.workDelayCause'),
            icon: <ChatFill />,
            value: {
              label: order?.works.workDelayCause ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.workDelayResponsible'),
            icon: <PersonFill />,
            value: {
              label: order?.works.workDelayResponsible ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.costEstimateSendDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.works.costEstimateSendDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.adductionCharacteristic'),
            icon: <Clipboard2CheckFill />,
            value: {
              label: order?.works.adductionCharacteristic ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.landComment'),
            icon: <ChatFill />,
            value: {
              label: convertDateFormat(order?.works.landComment) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.workCompletionDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.works.workCompletionDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.invoiceSendDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.orderCompleted.invoiceSendDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.invoicePaymentDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.orderCompleted.invoicePaymentDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.thawDate'),
            icon: <ClockFill />,
            value: {
              label: convertDateFormat(order?.orderCompleted.thawDate) ?? ''
            }
          },
          {
            title: traduction('orderDetails.adduction.clientPvStatus'),
            icon: <RssFill />,
            value: {
              label: order?.orderCompleted.clientPvStatus ?? ''
            }
          }
        ]
      }
    ]
  };
};

export default createAdductionDisplayConfig;
